// The five wish-block mutations behind `/wishes/:id/edit` (W51, split out of
// `routes/WishEditLayout.tsx`).
//
// ## Why this moved
//
// `WishEditLayout.tsx` sat at **15,301 bytes against a 15 KB route ceiling** —
// 59 bytes of slack, measured before W51 touched it. Adding the
// `queryKeys.dashboard.all` invalidation this sprint required (five writes, one
// line each) pushed it over, and the ceiling is a real rule
// (AGENTS §3 #10), not a warning.
//
// The fix was to move a cohesive unit, not to shave comments until it fit. These
// five mutations are one thing: a request, the toast that reports it, and the
// three caches it invalidates. Nothing else on the page touches them.
//
// ## The one thing worth reading twice
//
// `invalidateDashboard()` exists because of W51 D-1: the home page's 「最新動態」
// feed is a 30-second-stale query, and a block write that never invalidated it
// left the home page showing the previous state — the symptom the user reported.
// It sits here rather than at the call site so the six writers of wish data
// cannot drift apart on it.
//
// The per-call `onError` avoidance on `saveBlockMut` is explained in place: the
// mutation error reporter skips a call site that writes its own `onError`, so a
// roll-back-only handler would roll back *and* go silent.

import { useCallback, type Dispatch, type SetStateAction } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createWishBlock,
  defaultBlockData,
  deleteWishBlock,
  patchWishBlock,
  reorderBlocks,
  type BlockReorderItem,
} from "@/lib/api/wishBlocks";
import { patchWish, type WishBlockOut } from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";

export const WISH_QUERY_KEY_PREFIX = ["wishes", "detail"] as const;

export interface LocalBlockOverride {
  title: string;
  data: Record<string, unknown>;
}

/** What the page passes in, and what it gets back. */
export interface WishBlockMutations {
  saving: boolean;
  onSaveBlock: (input: {
    id: string;
    title: string;
    data: Record<string, unknown>;
  }) => void;
  onAddBlock: (input: {
    blockType: WishBlockOut["block_type"];
    title: string;
  }) => void;
  onDeleteBlock: (blockId: string) => void;
  onReorder: (input: { items: { id: string; position: number }[] }) => void;
  onSetCompletion: (input: {
    completionMode: "manual" | "aggregate";
    manualPercent: number | null;
    completionSources: string[];
  }) => void;
}

export function useWishBlockMutations({
  wishId,
  say,
  clearOverride,
  setLocalOverrides,
}: {
  wishId: string;
  say: (message: string) => void;
  clearOverride: (blockId: string) => void;
  setLocalOverrides: Dispatch<SetStateAction<Record<string, LocalBlockOverride>>>;
}): WishBlockMutations {
  const queryClient = useQueryClient();

  const invalidateDetail = () =>
    queryClient.invalidateQueries({
      queryKey: [...WISH_QUERY_KEY_PREFIX, wishId],
    });
  const invalidateList = () => queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
  // W51 (D-1) — see the file header for why this exists at all.
  const invalidateDashboard = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });

  const saveBlockMut = useMutation({
    // W49.0 C2. Was a raw `fetch` that threw `new Error(...)`. `messageFor()`
    // in `mutationErrorReporter.tsx` only recognises `ApiError`, so every
    // failure here was reported as the generic network-failure copy — while the
    // backend had been sending 「同類型多個區塊時必須填寫標題」 all along.
    // A 422 that can be predicted by reading the code was being presented as a
    // dead connection, which is the kind of wrong message that sends the next
    // person looking in the wrong place.
    mutationFn: async (input: { id: string; title: string; data: Record<string, unknown> }) => {
      try {
        return await patchWishBlock(wishId, input.id, {
          title: input.title,
          data: input.data,
        });
      } catch (err) {
        // W49.0 §2 — the failure exit: roll the optimistic value back so the
        // panel shows what the server actually holds instead of the edit that
        // was just refused.
        //
        // Deliberately **not** an `onError` option. The reporter treats a call
        // site that writes its own `onError` as having taken responsibility for
        // telling the user, and skips it — so an `onError` that only rolls back
        // would roll back *and* go quiet. Recovering here and rethrowing keeps
        // the error as an `ApiError` reaching the one place that speaks.
        clearOverride(input.id);
        throw err;
      }
    },
    onSuccess: () => {
      say("已儲存");
      invalidateDashboard();
    },
    onSettled: () => {
      invalidateDetail();
      invalidateList();
    },
  });

  const addBlockMut = useMutation({
    mutationFn: (input: { blockType: WishBlockOut["block_type"]; title: string }) =>
      createWishBlock(wishId, {
        block_type: input.blockType,
        title: input.title,
        data: defaultBlockData(input.blockType),
      }),
    onSuccess: () => {
      say("已新增區塊");
      invalidateDashboard();
    },
    onSettled: () => {
      invalidateDetail();
      invalidateList();
    },
  });

  const deleteBlockMut = useMutation({
    mutationFn: (blockId: string) => deleteWishBlock(wishId, blockId),
    onSuccess: () => {
      say("已刪除區塊");
      invalidateDashboard();
    },
    onSettled: () => {
      invalidateDetail();
      invalidateList();
    },
  });

  const reorderMut = useMutation({
    // `reorderBlocks` wraps the array in `{ items }` because that is the
    // `BlockReorderRequest` shape. This page used to post a bare array, which
    // Pydantic rejects with `422 VALIDATION_FAILED` — so **every** reorder,
    // desktop drag and mobile sheet alike, failed, while the local `order`
    // state made it look like it had worked. The correct function already
    // existed in this very file and was simply bypassed.
    mutationFn: (items: { id: string; position: number }[]) =>
      reorderBlocks(wishId, items as BlockReorderItem[]),
    onSuccess: () => {
      say("已更新排序");
      invalidateDashboard();
    },
    onSettled: () => {
      invalidateDetail();
    },
  });

  const setCompletionMut = useMutation({
    // Sent through `PATCH /wishes/{id}`, not `PATCH /wishes/{id}/completion-mode`.
    //
    // The completion-mode endpoint takes `{completion_mode, manual_percent}` and
    // is `extra="forbid"`, so the `completion_sources` this UI has always sent
    // alongside them is a 422 — verified against the running app, not inferred.
    // That made the per-source checkboxes dead: the panel 422'd on every change.
    //
    // The wish PATCH accepts all three fields and honours them literally, which
    // is also the closer fit for this screen: the mode endpoint *derives*
    // `completion_sources` by auto-populating from the blocks on a
    // manual → aggregate flip, so it would throw away exactly the selection the
    // person just made in those checkboxes.
    mutationFn: (input: {
      completionMode: "manual" | "aggregate";
      manualPercent: number | null;
      completionSources: string[];
    }) =>
      patchWish(wishId, {
        completion_mode: input.completionMode,
        manual_percent: input.manualPercent,
        completion_sources: input.completionSources,
      }),
    onSuccess: () => {
      say("已更新完成設定");
      invalidateDashboard();
    },
    onSettled: () => {
      invalidateDetail();
    },
  });

  const saving =
    saveBlockMut.isPending ||
    addBlockMut.isPending ||
    deleteBlockMut.isPending ||
    reorderMut.isPending ||
    setCompletionMut.isPending;

  const onSaveBlock = useCallback(
    (input: { id: string; title: string; data: Record<string, unknown> }) => {
      setLocalOverrides((prev) => ({
        ...prev,
        [input.id]: { title: input.title, data: input.data },
      }));
      saveBlockMut.mutate(input);
    },
    [saveBlockMut, setLocalOverrides],
  );

  const onAddBlock = useCallback(
    (input: { blockType: WishBlockOut["block_type"]; title: string }) => {
      addBlockMut.mutate(input);
    },
    [addBlockMut],
  );

  const onDeleteBlock = useCallback(
    (blockId: string) => {
      deleteBlockMut.mutate(blockId);
    },
    [deleteBlockMut],
  );

  const onReorder = useCallback(
    (input: { items: { id: string; position: number }[] }) => {
      reorderMut.mutate(input.items);
    },
    [reorderMut],
  );

  const onSetCompletion = useCallback(
    (input: {
      completionMode: "manual" | "aggregate";
      manualPercent: number | null;
      completionSources: string[];
    }) => {
      setCompletionMut.mutate(input);
    },
    [setCompletionMut],
  );

  return { saving, onSaveBlock, onAddBlock, onDeleteBlock, onReorder, onSetCompletion };
}
